// src/utils/firestoreService.js
// Funzioni Firestore allineate con Sidebar: cartelle custom, metadati file, tag, categorie,
// override titoli per link statici, appunti personali e reorder batch.

import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';

import { auth } from '../firebase';

const db = getFirestore();

/* ============ Helpers ============ */

const now = () => serverTimestamp();
const uidOrNull = () => auth?.currentUser?.uid || null;
const idFromPath = (storagePath) => encodeURIComponent(storagePath);

/* ============ Cartelle custom ============ */

export async function listCustomFolders() {
  const col = collection(db, 'customFolders');
  // si salva index opzionale; in mancanza, ordino per label
  const snap = await getDocs(col);
  const out = [];
  snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  // ordine: prima per index se presente, poi per label (richiesta: “in mezzo alle altre alfabetico”)
  out.sort((a, b) => {
    const ai = Number.isFinite(a.index) ? a.index : Number.MAX_SAFE_INTEGER;
    const bi = Number.isFinite(b.index) ? b.index : Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    return (a.label || '').localeCompare(b.label || '', 'it', { sensitivity: 'base' });
  });
  return out;
}

export async function createCustomFolder({ label, visibility = 'private', iconKey = 'folder' }) {
  const ownerUid = uidOrNull();
  const ref = doc(collection(db, 'customFolders'));
  await setDoc(ref, {
    label: label.trim(),
    visibility,
    iconKey,
    ownerUid,
    createdAt: now(),
  });
  return ref.id;
}

export async function updateCustomFolder(id, data) {
  const ref = doc(db, 'customFolders', id);
  await updateDoc(ref, { ...data, updatedAt: now() });
}

export async function deleteCustomFolder(id) {
  const ref = doc(db, 'customFolders', id);
  await deleteDoc(ref);
}

/* ============ Metadati File / Uploads ============ */

/** Upsert metadati di un file; id = encodeURIComponent(storagePath) */
export async function upsertFileMeta({
  storagePath,
  displayName,
  visualName,
  ownerUid,
  tags = [],
  categoryId = null,
  size = 0,
  index = null,
}) {
  const id = idFromPath(storagePath);
  const ref = doc(db, 'files', id);
  const cur = await getDoc(ref);
  if (cur.exists()) {
    await updateDoc(ref, {
      storagePath,
      displayName,
      visualName,
      ownerUid: ownerUid || uidOrNull(),
      tags,
      categoryId,
      size,
      index,
      updatedAt: now(),
    });
  } else {
    await setDoc(ref, {
      storagePath,
      displayName,
      visualName,
      ownerUid: ownerUid || uidOrNull(),
      tags,
      categoryId,
      size,
      index,
      createdAt: now(),
    });
  }
  return id;
}

/** Ritorna metadati file dove storagePath inizia con prefix (range query) */
export async function getFilesMetaByPrefix(prefix) {
  const col = collection(db, 'files');
  // “startsWith” con range: [prefix, prefix + \uf8ff]
  const q1 = query(col, where('storagePath', '>=', prefix), where('storagePath', '<=', prefix + '\uf8ff'));
  const snap = await getDocs(q1);
  const out = [];
  snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  return out;
}

export async function renameFileVisual(fileId, newName) {
  const ref = doc(db, 'files', fileId);
  await updateDoc(ref, { visualName: newName.trim(), updatedAt: now() });
}

export async function setFileCategory(fileId, folderIdOrNull) {
  const ref = doc(db, 'files', fileId);
  const v = folderIdOrNull || null;
  await updateDoc(ref, { categoryId: v, updatedAt: now() });
}

export async function setFileTags(fileId, tags = []) {
  const ref = doc(db, 'files', fileId);
  await updateDoc(ref, { tags: tags.filter(Boolean), updatedAt: now() });
}

/* ============ Reorder batch (cartelle e items) ============ */
/** Reindicizza più file: [{id, index}] */
export async function reorderItemsBatch(pairs = []) {
  const batch = writeBatch(db);
  pairs.forEach(({ id, index }) => {
    if (!id) return;
    const ref = doc(db, 'files', id);
    batch.update(ref, { index: Number.isFinite(index) ? index : null, updatedAt: now() });
  });
  await batch.commit();
}

/** Reindicizza più cartelle custom: [{id, index}] */
export async function reorderFoldersBatch(pairs = []) {
  const batch = writeBatch(db);
  pairs.forEach(({ id, index }) => {
    if (!id) return;
    const ref = doc(db, 'customFolders', id);
    batch.update(ref, { index: Number.isFinite(index) ? index : null, updatedAt: now() });
  });
  await batch.commit();
}

/* ============ Override titoli link statici (usati dal menu a pallini sui link) ============ */
/** Ritorna mappa { [linkId]: { title } } */
export async function getStaticLinkOverrides() {
  const col = collection(db, 'staticLinkOverrides');
  const snap = await getDocs(col);
  const map = {};
  snap.forEach((d) => {
    const v = d.data();
    if (v?.title) map[d.id] = { title: v.title };
  });
  return map;
}

export async function setStaticLinkOverrideTitle(linkId, title) {
  const ref = doc(db, 'staticLinkOverrides', linkId);
  await setDoc(ref, { title: title.trim(), updatedAt: now() }, { merge: true });
}

/* ============ Appunti personali (dialog Appunti) ============ */

export async function getUserNotes(uid) {
  if (!uid) return { text: '' };
  const ref = doc(db, 'userNotes', uid);
  const d = await getDoc(ref);
  if (!d.exists()) return { text: '' };
  const v = d.data();
  return { text: v?.text || '' };
}

export async function setUserNotes(uid, text) {
  if (!uid) throw new Error('uid mancante');
  const ref = doc(db, 'userNotes', uid);
  await setDoc(ref, { text: String(text || ''), updatedAt: now() }, { merge: true });
}
